import {
  AccountBox,
  AccountTree,
  ArrowBack,
  Business,
  Icecream,
  ListAlt,
  MeetingRoom,
} from "@mui/icons-material";
import {
  AppBar,
  Toolbar,
  IconButton,
  Menu,
  ListSubheader,
  MenuItem,
  ListItemIcon,
  Typography,
  styled,
  Avatar,
} from "@mui/material";
import sortBy from "lodash-es/sortBy";
import React from "react";
import { useState, MouseEvent, ReactElement } from "react";
import { FormattedMessage } from "react-intl";

import { useBreadcrumbs } from "~frontend/providers/BreadcrumbsProvider";
import {
  Tenant,
  useCurrentUser,
  UserSubaccountMembership,
  LoggedInUser,
} from "~frontend/providers/LoggedInUserProvider";
import {
  useCurrentUserSubaccountMemberships,
  useTenant,
} from "~frontend/providers/LoggedInUserProvider/context";
import { palette } from "~frontend/theme";
import { getLastUsedTimeForSubaccount } from "~frontend/util/subaccountUsage";

import { FlexBox } from "./FlexBox";
import { ExternalLink, InternalLink } from "./Link";

const HeaderLink = styled(InternalLink)({
  color: "inherit",
  textDecoration: "none",
  fontWeight: 700,
  "&:hover": {
    textDecoration: "none",
    color: "inherit",
  },
});

export const Subheader = styled(ListSubheader)({
  lineHeight: "24px",
  fontWeight: 600,
  fontSize: "16px",
  "&:not(:first-of-type)": {
    marginTop: "12px",
  },
});

export const MenuItemIcon = styled(ListItemIcon)({
  minWidth: "24px",
});

export const SiteHeader = styled("h1")({
  flexGrow: 1,
  overflow: "hidden",
  whiteSpace: "nowrap",
  textOverflow: "ellipsis",
  fontWeight: 500,
  fontSize: "16px",
});

export function AvatarButton({
  user,
  onClick,
}: {
  user: LoggedInUser;
  onClick: (event: MouseEvent<HTMLElement>) => void;
}): ReactElement {
  const initials = user.fullName.trim()[0];

  return (
    <IconButton
      onClick={onClick}
      sx={{
        "&:focus,:focus-visible": {
          outline: "none",
          "& :first-child": {
            outline: `2px solid ${palette.white}`,
            outlineOffset: "0px",
          },
        },
      }}
    >
      <Avatar
        sx={{
          height: 32,
          width: 32,
          backgroundColor: palette.blue,
          fontWeight: 700,
        }}
      >
        {initials}
      </Avatar>
    </IconButton>
  );
}

enum SubaccountDisplayMode {
  NONE,
  ALL,
  RECENT,
}

const MAX_SUBACCOUNTS_FOR_LIST = 5;

function getSubaccountDisplay(
  memberships: UserSubaccountMembership[],
  tenant: Tenant
): {
  displayMode: SubaccountDisplayMode;
  subaccountOptions: { id: string; label: string; slug: string }[];
} {
  if (!tenant.subaccountsEnabled) {
    return {
      displayMode: SubaccountDisplayMode.NONE,
      subaccountOptions: [],
    };
  }

  if (memberships.length < 2) {
    return {
      displayMode: SubaccountDisplayMode.NONE,
      subaccountOptions: [],
    };
  }

  if (memberships.length <= MAX_SUBACCOUNTS_FOR_LIST) {
    return {
      displayMode: SubaccountDisplayMode.ALL,
      subaccountOptions: sortBy(memberships, m => m.subaccount.name).map(m => ({
        id: m.subaccount.id,
        label: m.subaccount.name,
        slug: m.subaccount.slug,
      })),
    };
  }

  return {
    displayMode: SubaccountDisplayMode.RECENT,
    subaccountOptions: sortBy(
      memberships.filter(
        m => getLastUsedTimeForSubaccount(m.subaccount.id) != null
      ),
      m => -getLastUsedTimeForSubaccount(m.subaccount.id)!,
      m => m.subaccount.name
    )
      .slice(0, MAX_SUBACCOUNTS_FOR_LIST)
      .map(m => ({
        id: m.subaccount.id,
        label: m.subaccount.name,
        slug: m.subaccount.slug,
      })),
  };
}

export default function TopBar({
  icon,
  title,
  titleLink,
  leftOffset,
}: {
  icon?: ReactElement;
  title: string;
  titleLink: string;
  leftOffset: string;
}): ReactElement {
  const user = useCurrentUser()!;
  const memberships = useCurrentUserSubaccountMemberships()!;
  const tenant = useTenant()!;
  const { breadcrumbs, options } = useBreadcrumbs();

  const [menuOpen, setMenuOpen] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const { displayMode, subaccountOptions } = getSubaccountDisplay(
    memberships,
    tenant
  );

  const handleOpenUserMenu = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    setMenuOpen(true);
  };

  const handleCloseUserMenu = () => {
    setMenuOpen(false);
  };

  return (
    <AppBar
      elevation={0}
      sx={{
        width: `calc(100% - ${leftOffset})`,
        transition: "width 0.25s",
        backgroundColor: tenant.deactivatedAt ? palette.red : palette.darkBlue,
      }}
    >
      <Toolbar variant="dense">
        <SiteHeader>
          <FlexBox alignItems="center" gap={1}>
            {tenant.deactivatedAt && (
              <Typography>(DEACTIVATED TENANT)</Typography>
            )}
            {options.hideSubaccountTitle ? null : (
              <HeaderLink to={titleLink}>
                <FlexBox alignItems="center" gap={1}>
                  {icon}
                  {title}
                </FlexBox>
              </HeaderLink>
            )}
            <>
              {breadcrumbs.map(({ label, path, backArrow }, idx) => (
                <React.Fragment key={idx}>
                  {options.hideSubaccountTitle && idx === 0 ? null : (
                    <span> | </span>
                  )}

                  <HeaderLink
                    to={path}
                    sx={{
                      fontWeight: idx === breadcrumbs.length - 1 ? 400 : 700,
                    }}
                  >
                    <FlexBox alignItems="center" gap={1}>
                      {backArrow ? <ArrowBack /> : null}
                      {label}
                    </FlexBox>
                  </HeaderLink>
                </React.Fragment>
              ))}
            </>
          </FlexBox>
        </SiteHeader>
        {user ? (
          <div>
            <AvatarButton user={user} onClick={handleOpenUserMenu} />
            <Menu
              id="user-menu-appbar"
              anchorEl={anchorEl}
              anchorOrigin={{
                horizontal: "center",
                vertical: "bottom",
              }}
              keepMounted
              transformOrigin={{
                vertical: "top",
                horizontal: "center",
              }}
              open={menuOpen}
              onClick={handleCloseUserMenu}
              onClose={handleCloseUserMenu}
              MenuListProps={{
                sx: {
                  width: "300px",
                  "& .MuiMenuItem-root": {
                    fontSize: "14px",

                    "& .MuiSvgIcon-root": { color: palette.navy },
                    "> .MuiTypography-root": { pt: "2px" },
                  },
                },
              }}
            >
              <Subheader>{user.fullName}</Subheader>
              <MenuItem dense component={InternalLink} to="/user">
                <MenuItemIcon>
                  <AccountBox fontSize="small" />
                </MenuItemIcon>
                <Typography variant="inherit" noWrap>
                  <FormattedMessage
                    defaultMessage="My Profile"
                    description="Nav link to view and edit user profile"
                  />
                </Typography>
              </MenuItem>
              {user.tenantRole === "STAFF" && (
                <MenuItem dense component={InternalLink} to="/staff">
                  <MenuItemIcon>
                    <Icecream fontSize="small" />
                  </MenuItemIcon>

                  <Typography variant="inherit" noWrap>
                    STW Staff Admin
                  </Typography>
                </MenuItem>
              )}
              {user.tenantRole !== "NONE" && (
                <MenuItem dense component={InternalLink} to="/organization">
                  <MenuItemIcon>
                    <Business fontSize="small" />
                  </MenuItemIcon>

                  <Typography variant="inherit" noWrap>
                    <FormattedMessage
                      defaultMessage="Organization Settings"
                      description="Nav link to view and edit org-wide settings"
                    />
                  </Typography>
                </MenuItem>
              )}
              <MenuItem
                component={ExternalLink}
                dense
                href="/auth/logout"
                target="_self"
              >
                <MenuItemIcon>
                  <MeetingRoom fontSize="small" />
                </MenuItemIcon>
                <Typography variant="inherit" noWrap>
                  <FormattedMessage
                    defaultMessage="Logout"
                    description="Nav link to log out of the app"
                  />
                </Typography>
              </MenuItem>

              {displayMode !== SubaccountDisplayMode.NONE && [
                // Note that we're returning an array here rather than a
                // fragment because MUI <Menu> doesn't support fragments
                // (https://github.com/mui/material-ui/issues/16181)
                <Subheader key="subaccountHeader">
                  {displayMode === SubaccountDisplayMode.RECENT ? (
                    <FormattedMessage
                      defaultMessage="Recent Subaccounts"
                      description="Nav header for list of recently-used subaccounts"
                    />
                  ) : (
                    <FormattedMessage
                      defaultMessage="Subaccount"
                      description="Nav header for list of subaccounts"
                    />
                  )}
                </Subheader>,
                ...subaccountOptions.map(({ id, label, slug }) => (
                  <MenuItem
                    dense
                    component={InternalLink}
                    to={`/subaccounts/${slug}`}
                    key={id}
                  >
                    <MenuItemIcon>
                      <AccountTree fontSize="small" />
                    </MenuItemIcon>
                    <Typography variant="inherit" noWrap>
                      {label}
                    </Typography>
                  </MenuItem>
                )),
                ...(displayMode === SubaccountDisplayMode.RECENT
                  ? [
                      <MenuItem
                        dense
                        component={InternalLink}
                        to="/user/subaccounts"
                        key="allSubaccount"
                      >
                        <MenuItemIcon>
                          <ListAlt fontSize="small" />
                        </MenuItemIcon>

                        <Typography variant="inherit" noWrap>
                          <FormattedMessage
                            defaultMessage="All Subaccounts..."
                            description="Nav link to a list of all of the users' subaccounts"
                          />
                        </Typography>
                      </MenuItem>,
                    ]
                  : []),
              ]}
            </Menu>
          </div>
        ) : null}
      </Toolbar>
    </AppBar>
  );
}
